/** Compose a caller's cancellation with a request deadline. The caller owns
 * disposal after response handling, including failures while reading a body. */
export function createRequestDeadline(input: {
  signal?: AbortSignal | null;
  timeoutMs: number;
  timeoutReason: DOMException;
}): { signal: AbortSignal; dispose: () => void } {
  const controller = new AbortController();
  const forwardAbort = () => controller.abort(input.signal?.reason);
  const timer = setTimeout(() => controller.abort(input.timeoutReason), input.timeoutMs);
  if (input.signal?.aborted) forwardAbort();
  else input.signal?.addEventListener('abort', forwardAbort, { once: true });

  return {
    signal: controller.signal,
    dispose() {
      clearTimeout(timer);
      input.signal?.removeEventListener('abort', forwardAbort);
    },
  };
}

/** One owner for replaceable reads. Generation checks remain necessary when
 * an API or mock transport completes despite an abort. Finishing or cancelling
 * an older read must never retire its successor. */
export function createLatestRequestOwner() {
  let generation = 0;
  let active: AbortController | null = null;

  return {
    begin() {
      const startedGeneration = ++generation;
      active?.abort();
      const controller = new AbortController();
      active = controller;
      const finish = () => {
        if (active === controller) active = null;
      };
      return {
        signal: controller.signal,
        isCurrent: () => generation === startedGeneration && !controller.signal.aborted,
        finish,
        cancel() {
          controller.abort();
          finish();
        },
      };
    },
    cancel(reason?: unknown) {
      generation += 1;
      const previous = active;
      active = null;
      previous?.abort(reason);
    },
  };
}
